import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { api, formatApiError, money, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/admin/PageHead";
import StatusBadge from "@/components/common/StatusBadge";

const FILTERS = ["pending", "approved", "rejected", "all"];

export default function AdminRequests() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => api.get("/admin/requests", { params: { status } }).then(({ data }) => setItems(data)).catch(() => {}), [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (r, action) => {
    setBusyId(r.id);
    try {
      await api.post(`/admin/requests/${r.id}/${action}`);
      toast.success(`${r.type === "deposit" ? "Deposit" : "Withdrawal"} of ${money(r.amount)} ${action === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-requests-page">
      <PageHead title="Deposits & withdrawals" subtitle="Approving a deposit credits the client's cash; approving a withdrawal debits it." />
      <div className="mb-6 inline-flex rounded-full border border-line p-1">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setStatus(f)} data-testid={`admin-requests-filter-${f}`}
            className={`rounded-full px-4 py-2.5 text-xs capitalize sm:py-1.5 transition-colors duration-200 ${status === f ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{f}</button>
        ))}
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead><tr className="border-b border-line text-slate-500">
            <th className="px-6 py-4 font-medium">Submitted</th><th className="px-6 py-4 font-medium">Client</th><th className="px-6 py-4 font-medium">Type</th>
            <th className="px-6 py-4 font-medium">Method / note</th><th className="px-6 py-4 text-right font-medium">Amount</th><th className="px-6 py-4 text-right font-medium">Status</th>
          </tr></thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-line/60 last:border-0" data-testid={`admin-request-row-${r.id}`}>
                <td className="px-6 py-4 whitespace-nowrap text-slate-400">{fmtDate(r.created_at)}</td>
                <td className="px-6 py-4"><p className="text-white">{r.user_name}</p><p className="text-xs text-slate-500">{r.user_email}</p></td>
                <td className={`px-6 py-4 capitalize ${r.type === "deposit" ? "text-emerald-400" : "text-amber-500"}`}>{r.type}</td>
                <td className="px-6 py-4 text-slate-400">{r.method}{r.note && <span className="block text-xs text-slate-500">{r.note}</span>}</td>
                <td className="px-6 py-4 text-right font-mono text-white">{money(r.amount)}</td>
                <td className="px-6 py-4 text-right">
                  {r.status === "pending" ? (
                    <div className="flex justify-end gap-2">
                      <button disabled={busyId === r.id} onClick={() => act(r, "approve")} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-obsidian transition-colors hover:bg-emerald-400 disabled:opacity-50" data-testid={`admin-approve-${r.type}-btn-${r.id}`}><Check className="h-3.5 w-3.5" /> Approve</button>
                      <button disabled={busyId === r.id} onClick={() => act(r, "reject")} className="inline-flex items-center gap-1 rounded-full border border-red-500/40 px-3 py-1.5 text-xs text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50" data-testid={`admin-reject-${r.type}-btn-${r.id}`}><X className="h-3.5 w-3.5" /> Reject</button>
                    </div>
                  ) : <StatusBadge status={r.status} />}
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500" data-testid="admin-requests-empty">No {status === "all" ? "" : status} requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
